<script setup lang="ts">
import type { UnifiedChatContentRecommendation } from '~kui/types/contentRecommendation';
import type { RouteLocationRaw } from 'vue-router';

import { Badge } from '~ds/components/ui/badge';
import { Icon } from '~ds/components/ui/icon';
import { Text } from '~ds/components/ui/typography';
import { ICONS } from '~ds/lib/icons';
import { useIpxBackgroundStyle } from '~kui/composables/useIpxBackgroundStyle';
import { useI18n } from 'vue-i18n';

import type { AssetCardFacts } from './assetCardAccessibleName';

import { useAssetCardName } from './assetCardAccessibleName';
import { coinPillVariant, coinRushApplies } from './assetCardSponsorship';

type CollectionRec = Extract<UnifiedChatContentRecommendation, { type: 'learning_asset_collection' }>;

interface Props {
  rec: CollectionRec;
  fluid?: boolean;
  /** When set, the card renders as a crawlable `<a>` (NuxtLink) to this destination. */
  to?: string | RouteLocationRaw;
  /** AU-only Coin Rush promo pill; gated on the coin-rush-enabled flag by the container. */
  coinRush?: boolean;
  /** Refuses activation on the inner control itself, so the card also leaves the tab order. */
  disabled?: boolean;
}

const props = withDefaults(defineProps<Props>(), {
  fluid: false,
  to: undefined,
  coinRush: false,
  disabled: false,
});

const emit = defineEmits<{
  click: [];
}>();

const isCompleted = computed(() => props.rec.completedCount === props.rec.totalCount && props.rec.totalCount > 0);
const hasProgress = computed(() => props.rec.completedCount > 0);
const pill = computed(() => coinPillVariant(coinRushApplies(props.coinRush, props.rec)));
const { t } = useI18n();
const cardName = useAssetCardName();

/** What the card face shows, read by both the template and the card's name so the two cannot drift. */
const facts = computed<AssetCardFacts>(() => {
  const { partnerLogoUrl, partnerName, coins, duration, completedCount, totalCount } = props.rec;
  const sponsor = partnerLogoUrl ? partnerName : null;

  if (hasProgress.value) {
    return {
      completed: isCompleted.value,
      sponsor,
      progress: totalCount > 0 ? { completed: completedCount, total: totalCount } : null,
      duration,
    };
  }

  return { sponsor, coinRush: coinRushApplies(props.coinRush, props.rec), coins, duration };
});

const accessibleName = computed(() => cardName(t('unified.common.assetCard.view', { title: props.rec.name }), facts.value));

// Partner logo routed through ipx at its small display size (webp).
const partnerLogoStyle = useIpxBackgroundStyle(() => props.rec.partnerLogoUrl, { width: 80 });

// The clickable is `overflow-hidden` around a blurred plate, which the global Safari fix masks to
// its own box, so a focus outline on it is clipped away. The ring is drawn on this wrapper instead.
const rootClasses = computed(() =>
  twMerge(cva('relative rounded-2xl has-focus-visible:outline-3 has-focus-visible:outline-offset-3 has-focus-visible:outline-ring', {
    variants: {
      fluid: {
        true: 'w-full',
        false: 'w-fit',
      },
    },
  })({ fluid: props.fluid })),
);

const clickableClasses = computed(() =>
  twMerge(cva('relative block rounded-2xl overflow-hidden cursor-pointer h-45 bg-white border-2 border-white focus-visible:outline-none', {
    variants: {
      fluid: {
        true: 'w-full',
        false: 'min-w-80 max-w-80',
      },
    },
  })({ fluid: props.fluid })),
);
</script>

<template>
  <div :class="rootClasses">
    <KUICoreClickable
      :to="to"
      :disabled="disabled"
      :class="clickableClasses"
      :aria-label="accessibleName"
      @click="emit('click')"
    >
      <!-- No progress: full-card image background -->
      <template v-if="!hasProgress">
        <NuxtImg
          :src="rec.imageUrl"
          class="absolute inset-0 w-full h-full object-cover"
          role="presentation"
          alt=""
        />
        <span
          v-if="rec.partnerLogoUrl"
          class="absolute top-2 left-2 bg-white rounded-full size-10 bg-contain bg-center bg-no-repeat"
          :style="partnerLogoStyle"
        />
        <span v-if="facts.coins" class="absolute top-2 right-2 bg-secondary rounded-full p-1 flex items-center gap-0.5">
          <span class="text-xs font-medium ps-1">{{ t(pill.key, { coins: facts.coins }) }}</span>
          <NuxtImg
            alt=""
            role="presentation"
            class="w-4 h-auto"
            :src="pill.coinSrc"
          />
        </span>
        <span class="absolute inset-0 flex flex-col justify-end p-2">
          <KUICoreBlurredEdge
            as="span"
            blur-size="2xs"
            rounding="3xl"
            class="block w-fit"
          >
            <span class="block p-3 text-start">
              <Text
                as="span"
                weight="medium"
                class="block"
              >
                {{ rec.name }}
              </Text>
              <Badge
                v-if="facts.duration"
                as="span"
                class="mt-1"
                variant="outline"
              >
                <Icon
                  :name="ICONS.clock"
                  size="sm"
                  class="shrink-0"
                />
                {{ `${rec.duration} ${t('unified.common.min')}` }}
              </Badge>
            </span>
          </KUICoreBlurredEdge>
        </span>
      </template>

      <!-- Has progress: two-column layout -->
      <template v-else>
        <span class="flex flex-row w-full h-full">
          <span class="relative w-2/5 shrink-0">
            <NuxtImg
              :src="rec.imageUrl"
              class="w-full h-full object-cover"
              role="presentation"
              alt=""
            />
            <span
              v-if="rec.partnerLogoUrl"
              class="absolute top-2 left-2 bg-white rounded-full size-10 bg-contain bg-center bg-no-repeat"
              :style="partnerLogoStyle"
            />
          </span>

          <span class="flex flex-col p-3 flex-1 text-start">
            <span class="flex justify-between items-start gap-2">
              <Text
                as="span"
                weight="medium"
                class="block"
              >
                {{ rec.name }}
              </Text>
              <span v-if="isCompleted" class="bg-secondary size-8 rounded-full flex justify-center items-center shrink-0">
                <Icon :name="ICONS.tickSolid" size="lg" />
              </span>
            </span>

            <span class="mt-auto flex flex-col">
              <span class="flex gap-2">
                <Badge
                  v-if="facts.progress"
                  as="span"
                  class="h-full"
                  variant="outline"
                >
                  {{ t('unified.common.progressCount', facts.progress) }}
                </Badge>
                <Badge
                  v-if="facts.duration"
                  as="span"
                  class="h-full"
                  variant="outline"
                  :uppercase="false"
                >
                  <Icon
                    :name="ICONS.clock"
                    size="sm"
                    class="shrink-0"
                  />
                  {{ `${rec.duration}${t('unified.common.minShort')}` }}
                </Badge>
              </span>
              <KUITopicProgressBar
                v-if="facts.progress"
                class="mt-3"
                :progress-percent="Math.round((facts.progress.completed / facts.progress.total) * 100)"
                :sections="5"
                :height="5"
              />
            </span>
          </span>
        </span>
      </template>
    </KUICoreClickable>
  </div>
</template>
